import { utils } from '@hh/global.js'

const { debounce } = utils

const EARTH_RADIUS_MILES = 3958.8
const EARTH_RADIUS_KM = 6371

/**
 * Calculate distance between two coordinates using Haversine formula
 */
function getDistance(lat1, lon1, lat2, lon2, unit = 'imperial') {
  const R = unit === 'metric' ? EARTH_RADIUS_KM : EARTH_RADIUS_MILES
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function toRad(deg) {
  return deg * (Math.PI / 180)
}

function normalizeCountry(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,]/g, '')
}

function canonicalizeCountry(value) {
  const normalized = normalizeCountry(value)
  const aliases = {
    turkiye: 'turkey',
    turkey: 'turkey',
    'united states of america': 'united states',
    usa: 'united states',
    us: 'united states',
    uk: 'united kingdom',
  }
  return aliases[normalized] || normalized
}

class StoreLocatorPage extends HTMLElement {
  constructor() {
    super()

    this.drawerToggles = this.querySelectorAll('[data-store-drawer-toggle]')
    this.map = document.querySelector('store-locator-map')
    this.geocoderContainer = this.querySelector('[data-geocoder-container]')
    this.locateButton = this.querySelector('[data-locate-user]')
    this.filters = this.querySelectorAll('[data-stores-filter]')
    this.noStoresMessage = this.querySelector('[data-no-stores-message]')
    this.proximitySelect = this.querySelector('[data-proximity-radius]')
    this.drawerBackButtons = this.querySelectorAll('.store-locator-page__back')
    this.storeListings = this.querySelectorAll('[data-store-listing]')

    // Unit system: 'imperial' (miles) or 'metric' (kilometers)
    this.unitSystem = this.getAttribute('data-unit-system') || 'imperial'

    // State
    this.isLocationActive = false
    this.selectedStoreId = null
    this.selectedCountryName = null
    this.selectedCountryCode = null

    // Bind methods
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.handleToggleClick = this.handleToggleClick.bind(this)
    this.handleProximityChange = this.handleProximityChange.bind(this)
    this.handleLocateClick = this.handleLocateClick.bind(this)
    this.handleDrawerBackClick = this.handleDrawerBackClick.bind(this)
    this.handleStoreListingClick = this.handleStoreListingClick.bind(this)
    this.handleLocationSelected = this.handleLocationSelected.bind(this)
    this.handleLocationCleared = this.handleLocationCleared.bind(this)
    this.handleLocationLoading = this.handleLocationLoading.bind(this)
    this.handleLocationCancelled = this.handleLocationCancelled.bind(this)
    this.handleUserLocation = this.handleUserLocation.bind(this)
    this.handleLocationError = this.handleLocationError.bind(this)
    this.filterStores = this.filterStores.bind(this)
  }

  get distanceUnit() {
    return this.unitSystem === 'metric' ? 'km' : 'mi'
  }

  get distanceFormat() {
    return this.getAttribute('data-distance-format') || '{{distance}} {{unit}} away'
  }

  get locale() {
    return this.getAttribute('data-locale') || 'en'
  }

  /**
   * Format distance for display using the localized template from Liquid.
   * Uses locale-aware number formatting for decimal separator.
   */
  formatDistance(distance) {
    const formatted = Number(distance).toLocaleString(this.locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
    return this.distanceFormat
      .replace(/\{\{distance\}\}/g, formatted)
      .replace(/\{\{unit\}\}/g, this.distanceUnit)
  }

  connectedCallback() {
    // UI event listeners
    this.drawerToggles.forEach((toggle) => {
      toggle.addEventListener('click', this.handleToggleClick)
    })
    this.locateButton?.addEventListener('click', this.handleLocateClick)
    this.drawerBackButtons.forEach((button) => {
      button.addEventListener('click', this.handleDrawerBackClick)
    })
    this.storeListings.forEach((listing) => {
      listing.addEventListener('click', this.handleStoreListingClick)
    })

    this.filters.forEach((filter) => {
      filter.addEventListener('change', this.handleFilterChange)
    })
    if (![...this.filters].some((filter) => filter.checked)) {
      const allFilter = [...this.filters].find((filter) => filter.value === '__all')
      if (allFilter) allFilter.checked = true
    }
    this.proximitySelect?.addEventListener('change', this.handleProximityChange)

    // Map event listeners
    this.map?.addEventListener('location-selected', this.handleLocationSelected)
    this.map?.addEventListener('location-cleared', this.handleLocationCleared)
    this.map?.addEventListener('location-loading', this.handleLocationLoading)
    this.map?.addEventListener('location-cancelled', this.handleLocationCancelled)
    this.map?.addEventListener('user-location', this.handleUserLocation)
    this.map?.addEventListener('location-error', this.handleLocationError)

    // Wait for map to be ready before initializing geocoder
    if (this.map) {
      if (this.map.map) {
        this._initMapFeatures()
      } else {
        this.map.addEventListener('ready', () => this._initMapFeatures())
      }
    }
  }

  disconnectedCallback() {
    this.drawerToggles.forEach((toggle) => {
      toggle.removeEventListener('click', this.handleToggleClick)
    })
    this.locateButton?.removeEventListener('click', this.handleLocateClick)
    this.drawerBackButtons.forEach((button) => {
      button.removeEventListener('click', this.handleDrawerBackClick)
    })
    this.storeListings.forEach((listing) => {
      listing.removeEventListener('click', this.handleStoreListingClick)
    })
    this.filters.forEach((filter) => {
      filter.removeEventListener('change', this.handleFilterChange)
    })
    this.proximitySelect?.removeEventListener('change', this.handleProximityChange)

    this.map?.removeEventListener('location-selected', this.handleLocationSelected)
    this.map?.removeEventListener('location-cleared', this.handleLocationCleared)
    this.map?.removeEventListener('location-loading', this.handleLocationLoading)
    this.map?.removeEventListener('location-cancelled', this.handleLocationCancelled)
    this.map?.removeEventListener('user-location', this.handleUserLocation)
    this.map?.removeEventListener('location-error', this.handleLocationError)
  }

  /**
   * Initialize map-related features (geocoder)
   */
  _initMapFeatures() {
    if (this.geocoderContainer && this.map) {
      this.map.initGeocoder(this.geocoderContainer)
    }
  }

  // ==================== Event Handlers ====================

  handleFilterChange(e) {
    const currentFilter = e.currentTarget
    const allFilter = [...this.filters].find((filter) => filter.value === '__all')

    if (currentFilter.value === '__all') {
      currentFilter.checked = true
      this.filters.forEach((filter) => {
        if (filter !== currentFilter) filter.checked = false
      })
    } else if (currentFilter.checked) {
      this.filters.forEach((filter) => {
        if (filter !== currentFilter) filter.checked = false
      })
      if (allFilter) allFilter.checked = false
    } else {
      const hasSpecificFilter = [...this.filters].some(
        (filter) => filter.checked && filter.value !== '__all'
      )
      if (!hasSpecificFilter && allFilter) {
        allFilter.checked = true
      }
    }

    this.filterStores()
  }

  handleProximityChange() {
    this.filterStores()
  }

  handleToggleClick(e) {
    const trigger = e.currentTarget
    const storeHandle = trigger?.getAttribute('data-store-handle')
    if (!storeHandle) return

    this.map?.focusStore?.(storeHandle)

    trigger.dispatchEvent(
      new CustomEvent('openDrawer', {
        bubbles: true,
        composed: true,
        detail: { key: `store--${storeHandle}` },
      })
    )
  }

  handleLocateClick() {
    // Toggle behavior: if location is active, disable it
    if (this.isLocationActive) {
      this.disableLocationMode()
      return
    }

    // Clear geocoder (mutually exclusive with location)
    this.selectedStoreId = null
    this.selectedCountryName = null
    this.selectedCountryCode = null
    this.map?.clearGeocoder({ silent: true })

    this.locateButton?.classList.add('is-loading')
    this.locateButton?.classList.remove('is-error')

    // Trigger map's geolocation
    this.map?.locateUser()
  }

  handleDrawerBackClick() {
    if (!this.map) return
    this.map.restorePreviousView?.()
  }

  handleStoreListingClick(e) {
    // Keep native behavior for links and interactive elements inside cards.
    const el = e.target instanceof Element ? e.target : e.composedPath().find((n) => n instanceof Element)
    if (el?.closest('.store-locator-page__more-information-wrapper, a')) {
      return
    }

    const listing = e.currentTarget
    const trigger = listing.querySelector('[data-store-drawer-toggle]')
    if (!trigger) return

    // Reuse existing flow so drawer gets a valid js-drawer-trigger source.
    trigger.click()
  }

  handleLocationSelected(event) {
    // A geocoder result was selected
    const isStoreSelection = Boolean(event.detail?.isStore)
    this.selectedStoreId = isStoreSelection ? event.detail?.storeId || null : null
    this.selectedCountryName = event.detail?.countryName || null
    this.selectedCountryCode = event.detail?.countryCode || null
    this.isLocationActive = false
    this.locateButton?.classList.remove('is-loading')
    this.locateButton?.classList.remove('is-active')
    this.filterStores()
  }

  handleLocationCleared() {
    // Geocoder was cleared
    this.selectedStoreId = null
    this.selectedCountryName = null
    this.selectedCountryCode = null
    this.isLocationActive = false
    this.locateButton?.classList.remove('is-loading')
    this.locateButton?.classList.remove('is-active')
    this.filterStores()
  }

  handleLocationLoading() {
    this.locateButton?.classList.add('is-loading')
    this.locateButton?.classList.remove('is-error')
  }

  handleLocationCancelled() {
    this.locateButton?.classList.remove('is-loading')
  }

  handleUserLocation(event) {
    // User's geolocation was found
    this.locateButton?.classList.remove('is-loading')
    this.locateButton?.classList.remove('is-error')
    this.locateButton?.classList.add('is-active')
    this.isLocationActive = true
    this.selectedStoreId = null
    this.selectedCountryName = event.detail?.countryName || null
    this.selectedCountryCode = event.detail?.countryCode || null

    this.filterStores()
  }

  handleLocationError(event) {
    this.locateButton?.classList.remove('is-loading')
    this.locateButton?.classList.add('is-error')

    setTimeout(() => {
      this.locateButton?.classList.remove('is-error')
    }, 3000)

    console.warn('Geolocation error:', event.detail?.message)
  }

  disableLocationMode() {
    this.isLocationActive = false
    this.selectedStoreId = null
    this.selectedCountryName = null
    this.selectedCountryCode = null
    this.locateButton?.classList.remove('is-loading')
    this.locateButton?.classList.remove('is-active')

    // Clear selected location in map
    if (this.map) {
      this.map.cancelLocateUser?.()
      this.map.selectedLocation = null
    }

    this.filterStores()
    this.map?.fitToStores({ pushHistory: false })
  }

  // ==================== Filtering ====================

  filterStores() {
    const stores = this.map?.stores || []
    if (!stores.length) return

    const selectedLocation = this.map?.selectedLocation

    // Get active filters
    const filterValues = []
    this.filters.forEach((filter) => {
      if (filter.checked && filter.value !== '__all') {
        filterValues.push(filter.value)
      }
    })

    // Get proximity radius
    const radiusValue = this.proximitySelect?.value
    const radius = radiusValue ? parseFloat(radiusValue) : null

    // Calculate distances if we have a selected location
    const storesWithDistance = stores.map((store) => {
      let distance = null
      if (selectedLocation && store.coordinates?.length === 2) {
        distance = getDistance(
          selectedLocation.lat,
          selectedLocation.lng,
          store.coordinates[1],
          store.coordinates[0],
          this.unitSystem
        )
      }
      return { ...store, distance }
    })

    // Filter stores
    const activeStores = storesWithDistance.filter((store) => {
      const isSelectedStoreMatching = this.selectedStoreId
        ? String(store.id) === String(this.selectedStoreId)
        : true

      // Store type filter
      const isFiltersMatching = filterValues.length
        ? filterValues.includes(store.boutique_type)
        : true

      // Proximity filter
      let isProximityMatching = true
      if (radius && selectedLocation) {
        if (store.distance !== null) {
          isProximityMatching = store.distance <= radius
        } else {
          isProximityMatching = false
        }
      }

      // If Any distance is selected for a geocoded location, constrain to the query country.
      let isCountryMatching = true
      if (!radius && selectedLocation && (this.selectedCountryName || this.selectedCountryCode)) {
        const storeCountry = canonicalizeCountry(store.country)
        const targetCountryName = canonicalizeCountry(this.selectedCountryName)
        const targetCountryCode = normalizeCountry(this.selectedCountryCode)
        isCountryMatching = Boolean(
          storeCountry &&
          (storeCountry === targetCountryName ||
            storeCountry.includes(targetCountryName) ||
            targetCountryName.includes(storeCountry) ||
            storeCountry === targetCountryCode)
        )
      }

      return isSelectedStoreMatching && isFiltersMatching && isProximityMatching && isCountryMatching
    })

    // Sort by distance if we have a location selected
    if (selectedLocation) {
      activeStores.sort((a, b) => {
        if (a.distance === null) return 1
        if (b.distance === null) return -1
        return a.distance - b.distance
      })
    }

    // Fit map when result set is explicitly narrowed (radius/filter/store/country-bounded any-distance).
    const isCountryBoundAnyDistance =
      !radius && selectedLocation && (this.selectedCountryName || this.selectedCountryCode)
    const shouldFitMap =
      !selectedLocation ||
      Boolean(radius) ||
      filterValues.length > 0 ||
      Boolean(this.selectedStoreId) ||
      isCountryBoundAnyDistance
    this.map?.updateActiveStores(activeStores, { fitMap: shouldFitMap })

    // Update store list visibility and distance display
    const shouldApplyDistanceOrder = Boolean(selectedLocation)
    const allStoreElements = this.querySelectorAll('[data-store-listing]')
    const orderMap = new Map(activeStores.map((store, index) => [String(store.id), index]))
    allStoreElements.forEach((storeElement) => {
      const storeId = storeElement.getAttribute('data-store-handle')
      const storeData = activeStores.find((s) => s.id === storeId)
      const isActive = !!storeData

      storeElement.style.display = isActive ? '' : 'none'
      storeElement.style.order =
        shouldApplyDistanceOrder && isActive
          ? String(orderMap.get(String(storeId)) ?? 9999)
          : ''

      // Update distance display
      const distanceEl = storeElement.querySelector('[data-store-distance]')
      if (distanceEl) {
        if (
          storeData?.distance !== null &&
          storeData?.distance !== undefined &&
          Number.isFinite(storeData.distance) &&
          storeData.distance > 0
        ) {
          distanceEl.textContent = this.formatDistance(storeData.distance)
          distanceEl.classList.remove('hidden')
        } else {
          distanceEl.classList.add('hidden')
        }
      }
    })

    // Show/hide no stores message
    if (this.noStoresMessage) {
      this.noStoresMessage.classList.toggle('hidden', activeStores.length > 0)
    }
  }
}

if (!customElements.get('store-locator-page')) {
  customElements.define('store-locator-page', StoreLocatorPage)
}
